<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { useChatStore } from '../stores/chatStore';
import type { PropType } from 'vue';
import type { MessageDataPage, MessageDataScenarios, FollowupQuestion } from '../utils/type';

const { messages, t } = useI18n()
const chatStore = useChatStore();
const config = useRuntimeConfig();

const props = defineProps({
    user: String,
    author: String,
    answer: Boolean,
    type: String,
    reviewPattern: Number,
    keywordStr: String,
    keywordUrl: String,
    questions: {
        type: Array as PropType<FollowupQuestion[]>,
        default: () => [],
    },
    text: {
        type: String,
        required: true,
    },
    direction: {
        type: String,
        required: true,
        validator: (value: string) => ['left', 'right'].includes(value),
    },
    scenarios: {
        type: Array as PropType<MessageDataScenarios[]>,
        default: () => [],
    },
    pages: {
        type: Array as PropType<MessageDataPage[]>,
        default: () => [],
    },
    historyId: {
        type: String,
        default: '',
    },
    isGoodReviewed: {
        type: Boolean,
        default: false,
    },
    isBadReviewed: {
        type: Boolean,
        default: false,
    },
    copyText: {
        type: String,
        required: true
    },
    language: {
        type: String,
        required: true
    },
    imageUrl: {
        type: String,
        default: ''
    },
    IsDspUser: {
        type: Boolean,
        default: true
    },
    isFollowUp: {
        type: Boolean,
        default: false
    },
    followUpTaskId: {
        type: String,
        default: ''
    }
});
</script>

<template>
    <div class="chat-container tracking-[1px] px-[2.9rem]"
        :class="props.direction, { 'justify-center': props.type != 'typing', ['width-auto']: props.type == 'text' && props.imageUrl, ['-mt-3']: !props.IsDspUser }">
        <div class="chat-bubble" :class="{ 'w-full': props.type != 'typing', [`type-${props.type}`]: true }">
            <template v-if="props.type == 'typing'">
                <Typing />
            </template>
            <template v-else>
                <div v-if="props.isFollowUp"
                    class="flex items-center gap-1 text-[11px] font-semibold uppercase text-design-red mb-2">
                    <Icon name="material-symbols:subdirectory-arrow-right-rounded" size="14" />
                    <span>{{ t('followUpBadge') }}</span>
                </div>
                <img v-if="props.imageUrl" :src="props.imageUrl" class="max-h-[200px]" alt="" title="" />
                <p class="text-base leading-6 break-words chat-answer-container"
                    :class="chatStore.createLanguageClass(props.language)" v-html="props.reviewPattern == 0 ? props.text : messages[props.language.replace('-', ''
                    )]['reviewFieldText']"></p>
                <ScenarioButton :isShow="props.user == config.public.botName" :scenarios="props.scenarios"
                    :language="props.language" />
                <RelatedInfoButton :isShow="props.answer && props.pages.length > 0 && props.type == 'text'"
                    :pages="props.pages" :language="language" :historyId="props.historyId" />
                <Questions :isShow="props.user == config.public.botName" :questions="props.questions"
                    :language="props.language" />
                <Keyword
                    :isShow="props.answer && props.pages.length == 0 && props.type == 'text' && (props.keywordUrl?.length ?? 0 > 0)"
                    :language="language" :keywordStr="props.keywordStr" :keywordUrl="props.keywordUrl" />
                <ReviewButton :isShow="props.answer && props.type == 'text' && props.historyId != ''" :text="props.text"
                    :pages="props.pages" :historyId="props.historyId" :isGoodReviewed="props.isGoodReviewed"
                    :isBadReviewed="props.isBadReviewed" :copyText="props.copyText"
                    :reviewPattern="props.reviewPattern" />
            </template>
        </div>
    </div>
</template>

<style scoped>
@reference "../assets/css/tailwind.css";

sup {
    background-color: blue !important;
}

.chat-container {
    display: flex;
}

.chat-container.right {
    justify-content: flex-end;
}


.chat-bubble {
    background-color: #ffffff;
    border: 2px solid #ffffff;
    border-radius: 5px;
    padding: 15px;
    position: relative;
    @apply drop-shadow-md;
}

.chat-container.left .chat-bubble::after {
    content: '';
    position: absolute;
    top: 10%;
    left: -8px;
    transform: translateY(-10%);
    border-width: 8px 8px 8px 0;
    border-style: solid;
    border-color: transparent #ffffff transparent transparent;
    display: block;
    width: 0;
}

.chat-container.left .chat-bubble::before {
    content: '';
    position: absolute;
    top: 10%;
    left: -10px;
    transform: translateY(-10%);
    border-width: 10px 10px 10px 0;
    border-style: solid;
    border-color: transparent #ffffff transparent transparent;
    display: block;
    width: 0;
}

.chat-container.right .chat-bubble::after {
    content: '';
    position: absolute;
    top: 10%;
    right: -8px;
    transform: translateY(-10%);
    border-width: 8px 0 8px 8px;
    border-style: solid;
    border-color: transparent transparent transparent #ffffff;
    display: block;
    width: 0;
}

.chat-container.right .chat-bubble::before {
    content: '';
    position: absolute;
    top: 10%;
    right: -10px;
    transform: translateY(-10%);
    border-width: 10px 0 10px 10px;
    border-style: solid;
    border-color: transparent transparent transparent #ffffff;
    display: block;
    width: 0;
}

:deep(sup) {
    font-size: 0.6rem;
    vertical-align: baseline;
    background-color: #fff0f0;
    padding: 2px 4px;
    margin-left: 1px;
    border-radius: 4px;
    cursor: pointer;
    border: 1px solid #D53939;
}

.type-system_text {
    @apply bg-[#fef9c3];
    @apply border-[#fef9c3];
    @apply text-[#854d0e];
    @apply italic;
}

.chat-container.left .chat-bubble.type-system_text::before {
    border-color: transparent #fef9c3 transparent transparent;
}

.chat-container.left .chat-bubble.type-system_text::after {
    border-color: transparent #fef9c3 transparent transparent;
}

.chat-container.width-auto {
    justify-content: start;

    .chat-bubble {
        width: auto;
    }
}

/* ================================
   ベースレイアウト
================================ */
:deep(.chat-answer-container) {
    /* font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI",
        "Helvetica Neue", Arial, "Yu Gothic", "Hiragino Kaku Gothic ProN",
        Meiryo, sans-serif;
    font-size: 16px;
    line-height: 1.7;
    color: #222222;
    background-color: #ffffff;
    max-width: 800px;
    margin: 1.5rem auto;
    padding: 1.5rem 2rem;
    box-sizing: border-box;
    border-radius: 12px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06); */
    /* padding: 0 1.5rem; */
}

/* @media (max-width: 600px) {
    :deep(.chat-answer-container) {
        font-size: 15px;
        padding: 1.25rem 1rem;
        margin: 1rem 0.5rem;
        border-radius: 10px;
    }
} */

/* ================================
   ベーステキスト
================================ */
:deep(.chat-answer-container p) {
    margin: 0 0 0.9rem;
    color: #404040;
    line-height: 1.7;
}

:deep(.chat-answer-container strong),
:deep(.chat-answer-container b) {
    font-weight: 600;
}

/* 最後の要素の余白調整 */
:deep(.chat-answer-container > :last-child) {
    margin-bottom: 0;
}

/* ================================
   見出し
================================ */
:deep(.chat-answer-container h1),
:deep(.chat-answer-container h2),
:deep(.chat-answer-container h3),
:deep(.chat-answer-container h4),
:deep(.chat-answer-container h5),
:deep(.chat-answer-container h6) {
    font-weight: 600;
    line-height: 1.4;
    margin: 1.8rem 0 0.7rem;
    color: #171717;
}

/* h1 */
:deep(.chat-answer-container h1) {
    font-size: 1.6rem;
    border-bottom: 2px solid #e3e8f0;
    padding-bottom: 0.4rem;
    letter-spacing: -0.025em;
}

:deep(.chat-bubble .chat-answer-container h1:first-of-type) {
    margin-top: 0;
}

:deep(.chat-bubble .chat-answer-container h2:first-of-type) {
    margin-top: 0;
}


@media (min-width: 768px) {
    :deep(.chat-answer-container h1) {
        font-size: 1.75rem;
        line-height: 1.25;
    }
}

/* h2 */
:deep(.chat-answer-container h2) {
    margin-top: 2rem;
    font-size: 1.4rem;
    line-height: 1.75rem;
    font-weight: 600;
    color: #262626;
    border-left: 4px solid #3b82f6;
    /* 提案スタイルに合わせて青系 */
    padding-left: 0.75rem;
}

@media (min-width: 768px) {
    :deep(.chat-answer-container h2) {
        font-size: 1.5rem;
        line-height: 2rem;
    }
}

/* h3 */
:deep(.chat-answer-container h3) {
    font-size: 1.2rem;
    color: #1f2933;
}

/* h4〜 */
:deep(.chat-answer-container h4) {
    font-size: 1.05rem;
    color: #374151;
}

/* h2 直後のリストの余白調整 */
:deep(.chat-answer-container h2 + ul),
:deep(.chat-answer-container h2 + ol) {
    margin-top: 1rem;
}

/* ================================
   リスト
================================ */
/* 第一階層のリスト（●カスタムドット） */
:deep(.chat-answer-container ul) {
    margin: 0.4rem 0 0.9rem 0;
    list-style: none;
    padding-left: 0;
}

:deep(.chat-answer-container ul > li) {
    position: relative;
    padding-left: 1.5rem;
    color: #262626;
    line-height: 1.7;
}

:deep(.chat-answer-container ul > li + li) {
    margin-top: 0.75rem;
}

:deep(.chat-answer-container ul > li::before) {
    content: "";
    position: absolute;
    left: 0.5rem;
    top: 0.7rem;
    width: 0.4rem;
    height: 0.4rem;
    border-radius: 9999px;
    background-color: #3b82f6;
    /* 提案スタイルのアクセント色 */
}

/* 第二階層のリスト */
:deep(.chat-answer-container ul ul) {
    margin-top: 0.6rem;
    padding-left: 0;
}

:deep(.chat-answer-container ul ul > li) {
    position: relative;
    padding-left: 1.4rem;
    color: #4b5563;
    font-size: 0.95rem;
    line-height: 1.6;
}

:deep(.chat-answer-container ul ul > li + li) {
    margin-top: 0.5rem;
}

:deep(.chat-answer-container ul ul > li::before) {
    content: "";
    position: absolute;
    left: 0.35rem;
    top: 0.62rem;
    width: 0.3rem;
    height: 0.3rem;
    border-radius: 9999px;
    border: 2px solid #3b82f6;
    background-color: transparent;
}

/* 番号付きリスト */
:deep(.chat-answer-container ol) {
    margin: 0.4rem 0 0.9rem 1.4rem;
    padding-left: 1.2rem;
    color: #262626;
    line-height: 1.7;
}

:deep(.chat-answer-container ol > li + li) {
    margin-top: 0.75rem;
}

/* ダークモード用（任意） */
.dark :deep(.chat-answer-container ul > li) {
    color: #e5e7eb;
}

.dark :deep(.chat-answer-container ul > li::before) {
    background-color: #60a5fa;
}

.dark :deep(.chat-answer-container ul ul > li) {
    color: #cbd5f5;
}

.dark :deep(.chat-answer-container ul ul > li::before) {
    border-color: #60a5fa;
}

.dark :deep(.chat-answer-container ol) {
    color: #d1d5db;
}

/* ================================
   引用・注意ボックス
================================ */
:deep(.chat-answer-container blockquote) {
    margin: 0 0 1rem;
    padding: 0.6rem 0.9rem;
    border-left: 4px solid #22c55e;
    background-color: #f8fafc;
    color: #374151;
    border-radius: 6px;
}

/* ラベル風ボックス（拡張用クラス） */
:deep(.chat-answer-container .notice) {
    border-left: 4px solid #f59e0b;
    background-color: #fffbeb;
    padding: 0.6rem 0.9rem;
    border-radius: 6px;
    margin: 0 0 1rem;
}

:deep(.chat-answer-container .info) {
    border-left: 4px solid #3b82f6;
    background-color: #eff6ff;
    padding: 0.6rem 0.9rem;
    border-radius: 6px;
    margin: 0 0 1rem;
}

/* ================================
   リンク
================================ */
:deep(.chat-answer-container a) {
    color: #2563eb;
    text-decoration: none;
    border-bottom: 1px solid rgba(37, 99, 235, 0.35);
}

:deep(.chat-answer-container a:hover),
:deep(.chat-answer-container a:focus) {
    border-bottom-color: rgba(37, 99, 235, 0.9);
}

/* ================================
   コード
================================ */
:deep(.chat-answer-container code) {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
        "Courier New", monospace;
    font-size: 0.9em;
    padding: 0.1em 0.35em;
    border-radius: 4px;
    background-color: #f3f4f6;
    color: #111827;
}

:deep(.chat-answer-container pre) {
    margin: 0 0 1rem;
    padding: 0.75rem 0.9rem;
    background-color: #0f172a;
    color: #e5e7eb;
    border-radius: 8px;
    overflow-x: auto;
    font-size: 0.9rem;
}

:deep(.chat-answer-container pre code) {
    background: none;
    padding: 0;
    color: inherit;
}

/* ================================
   テーブル
================================ */
:deep(.chat-answer-container table) {
    width: 100%;
    border-collapse: collapse;
    margin: 0.75rem 0 1rem;
    font-size: 0.95rem;
}

:deep(.chat-answer-container th),
:deep(.chat-answer-container td) {
    border: 1px solid #e5e7eb;
    padding: 0.5rem 0.6rem;
    text-align: left;
}

:deep(.chat-answer-container th) {
    background-color: #f3f4f6;
    font-weight: 600;
}

:deep(.chat-answer-container tbody tr:nth-child(even)) {
    background-color: #f9fafb;
}

/* ================================
   水平線
================================ */
:deep(.chat-answer-container hr) {
    border: 0;
    border-top: 1px solid #d1d5db;
    margin: 1.5rem 0;
}

:deep(.chat-answer-container hr + p) {
    margin-top: 0.5rem;
}

/* ================================
   補足テキスト
================================ */
:deep(.chat-answer-container small) {
    font-size: 0.85em;
    color: #6b7280;
}
</style>
